import { service } from '@ember/service';
import Route from '@ember/routing/route';
import type { AuthenticatedSyfRouteModel } from '../syf';
import type { MatchPlanBenefitYear } from 'tio-common/types/secure-your-future';
import type AccountTransactionModel from 'tio-common/models/account-transaction';
import type MatchPaymentModel from 'tio-common/models/match-payment';
import type RouterService from '@ember/routing/router-service';
import type SessionContextService from 'tio-employee/services/session-context';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';
import type PersonModel from 'tio-common/models/person';

export default class AuthenticatedSyfDashboardRoute extends Route {
  @service declare store: StoreService;
  @service declare sessionContext: SessionContextService;
  @service declare router: RouterService;

  queryParams = {
    benefitYear: { refreshModel: true, replace: true },
  };

  beforeModel() {
    if (!this.sessionContext.currentEmployee.matchParticipant?.isEnrolled) {
      this.router.transitionTo('authenticated.syf.enrollment');
    }
  }

  async model(params: { benefitYear: string }) {
    const person = await this.store.findRecord<PersonModel>(
      'person',
      // @ts-expect-error type models
      this.sessionContext.user.person.id,
      {
        include: 'accounts.financial-institution',
        reload: true,
      }
    );

    const parentModel = this.modelFor('authenticated.syf') as AuthenticatedSyfRouteModel;
    if (!parentModel) {
      this.router.transitionTo('authenticated.syf.enrollment');
      // Transition aborts this model hook, but TypeScript needs a return
      throw new Error('Redirecting to enrollment');
    }

    const { matchParticipant } = parentModel;
    await matchParticipant.fetchInsights();

    let { benefitYear } = params;
    if (!benefitYear) {
      const currentDate = new Date();
      const metadata = matchParticipant.matchPlan.metadata;

      // Find the most recent benefit year that has not yet ended
      const applicableBenefitYear = Object.values(metadata.benefit_years).find(
        (yearData: MatchPlanBenefitYear) => {
          const cutoffDate = new Date(yearData.final_certification_date);
          return currentDate <= cutoffDate;
        }
      );

      benefitYear = applicableBenefitYear?.benefit_year.toString() ?? '2024';

      this.router.transitionTo({ queryParams: { benefitYear } });
    }

    const [matchPayments, accountTransactions] = await Promise.all([
      this.store.query<MatchPaymentModel>('match-payment', {
        filter: {
          'match-participant': matchParticipant.id,
          'transaction-year': benefitYear,
          'include-discarded': false,
        },
        include: 'account-transaction.financial-institution',
        sort: '-transaction-date',
        page: { limit: 200 },
      }),
      this.store.query<AccountTransactionModel>('account-transaction', {
        filter: {
          person: person.id,
          'transaction-year': benefitYear,
          'include-discarded': false,
        },
        sort: '-transaction-date',
        page: { limit: 200 },
      }),
    ]);

    return {
      accounts: person.activeAccounts,
      matchParticipant: matchParticipant,
      matchPlan: matchParticipant.matchPlan,
      matchPayments: matchPayments.slice(),
      accountTransactions: accountTransactions.slice(),
    };
  }
}
